import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import {
  ProductForm,
  type ProductFormValues,
} from "@/inventory/components/product-form"
import {
  createProductFn,
  listCarriersFn,
  listProductsFn,
  updateProductFn,
} from "@/inventory/rpc"
import { Badge } from "@/ui/badge"
import { Button } from "@/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog"
import { cn } from "@/ui/utils"

export const Route = createFileRoute("/_inventory-layout/inventory/products")({
  component: ProductsPage,
})

interface ProductRow {
  id: number
  type: "sim" | "device"
  name: string
  brand: string | null
  model: string | null
  defaultCarrierId: number | null
  isActive: boolean
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message
  }
  return "Something went wrong"
}

function ProductsPage() {
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<ProductRow | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const productsQuery = useQuery({
    queryKey: ["products", { includeInactive: true }],
    queryFn: () => listProductsFn({ data: { includeInactive: true } }),
  })

  const carriersQuery = useQuery({
    queryKey: ["carriers", { includeInactive: true }],
    queryFn: () => listCarriersFn({ data: { includeInactive: true } }),
  })

  const carrierNameById = useMemo(() => {
    const map = new Map<number, string>()
    for (const carrier of carriersQuery.data ?? []) {
      map.set(carrier.id, carrier.name)
    }
    return map
  }, [carriersQuery.data])

  const createProduct = useMutation({
    mutationFn: (values: ProductFormValues) =>
      createProductFn({
        data: {
          type: values.type,
          name: values.name,
          brand: values.brand || null,
          model: values.model || null,
          defaultCarrierId: values.defaultCarrierId,
        },
      }),
    onSuccess: async () => {
      setFormError(null)
      setDialogOpen(false)
      setEditing(null)
      await queryClient.invalidateQueries({ queryKey: ["products"] })
    },
    onError: (error) => setFormError(getErrorMessage(error)),
  })

  const updateProduct = useMutation({
    mutationFn: (input: { id: number; values: ProductFormValues }) =>
      updateProductFn({
        data: {
          id: input.id,
          name: input.values.name,
          brand: input.values.brand || null,
          model: input.values.model || null,
          defaultCarrierId: input.values.defaultCarrierId,
          isActive: input.values.isActive,
        },
      }),
    onSuccess: async () => {
      setFormError(null)
      setDialogOpen(false)
      setEditing(null)
      await queryClient.invalidateQueries({ queryKey: ["products"] })
    },
    onError: (error) => setFormError(getErrorMessage(error)),
  })

  const isSubmitting = createProduct.isPending || updateProduct.isPending
  const products = (productsQuery.data ?? []) as ProductRow[]

  function openCreate() {
    setEditing(null)
    setFormError(null)
    setDialogOpen(true)
  }

  function openEdit(product: ProductRow) {
    setEditing(product)
    setFormError(null)
    setDialogOpen(true)
  }

  function closeDialog() {
    if (isSubmitting) return
    setDialogOpen(false)
    setEditing(null)
    setFormError(null)
  }

  async function handleSubmit(values: ProductFormValues) {
    if (editing) {
      await updateProduct.mutateAsync({ id: editing.id, values })
    } else {
      await createProduct.mutateAsync(values)
    }
  }

  const listError = productsQuery.isError
    ? getErrorMessage(productsQuery.error)
    : carriersQuery.isError
      ? getErrorMessage(carriersQuery.error)
      : null

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-semibold text-2xl tracking-tight">Products</h1>
          <p className="mt-1 text-muted-foreground text-sm">
            Catalog of SIM and device products for receiving inventory.
          </p>
        </div>
        <Button type="button" onClick={openCreate}>
          New product
        </Button>
      </div>

      {listError ? (
        <p className="text-destructive text-sm" role="alert">
          {listError}
        </p>
      ) : null}

      {productsQuery.isLoading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : products.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No products yet. Create your first product to get started.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Brand</th>
                <th className="px-3 py-2 font-medium">Model</th>
                <th className="px-3 py-2 font-medium">Default carrier</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {products.map((product) => (
                <tr key={product.id} className="hover:bg-muted/30">
                  <td
                    className={cn(
                      "px-3 py-2 font-medium",
                      !product.isActive && "text-muted-foreground",
                    )}
                  >
                    {product.name}
                  </td>
                  <td className="px-3 py-2 capitalize">{product.type}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {product.brand || "—"}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {product.model || "—"}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {product.defaultCarrierId != null
                      ? (carrierNameById.get(product.defaultCarrierId) ??
                        `Carrier #${product.defaultCarrierId}`)
                      : "—"}
                  </td>
                  <td className="px-3 py-2">
                    {product.isActive ? (
                      <Badge variant="secondary">Active</Badge>
                    ) : (
                      <Badge variant="outline">Inactive</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(product)}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) closeDialog()
          else setDialogOpen(true)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit product" : "New product"}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? "Update product details or activate/deactivate it."
                : "Add a SIM or device product to the catalog."}
            </DialogDescription>
          </DialogHeader>
          <ProductForm
            key={editing ? `edit-${editing.id}` : "create"}
            mode={editing ? "edit" : "create"}
            carriers={carriersQuery.data ?? []}
            initialValues={
              editing
                ? {
                    type: editing.type,
                    name: editing.name,
                    brand: editing.brand ?? "",
                    model: editing.model ?? "",
                    defaultCarrierId: editing.defaultCarrierId,
                    isActive: editing.isActive,
                  }
                : undefined
            }
            isSubmitting={isSubmitting}
            error={formError}
            onSubmit={handleSubmit}
            onCancel={closeDialog}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}
